import { useCallback, useMemo } from 'react';
import { useMsal, useAccount } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { loginRequest, ADMIN_GROUP_ID } from './msalConfig';

export interface AuthUser {
  name: string;
  email: string;
  objectId: string;
  isAdmin: boolean;
}

export function useAuth() {
  const { instance, accounts } = useMsal();
  const account = useAccount(accounts[0] || {});

  const isAuthenticated = accounts.length > 0;

  const user: AuthUser | null = useMemo(() => {
    if (!account) return null;

    const idTokenClaims = account.idTokenClaims as Record<string, unknown> | undefined;
    const groups = (idTokenClaims?.groups as string[]) || [];
    const roles = (idTokenClaims?.roles as string[]) || [];

    const isAdmin =
      groups.includes(ADMIN_GROUP_ID) || roles.includes(ADMIN_GROUP_ID);

    return {
      name: account.name || '',
      email: account.username || '',
      objectId: account.localAccountId || '',
      isAdmin,
    };
  }, [account]);

  const login = useCallback(async () => {
    try {
      await instance.loginPopup(loginRequest);
    } catch (error) {
      console.error('Login failed:', error);
    }
  }, [instance]);

  const logout = useCallback(async () => {
    try {
      await instance.logoutPopup({
        postLogoutRedirectUri: window.location.origin,
      });
    } catch (error) {
      console.error('Logout failed:', error);
    }
  }, [instance]);

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    if (!account) return null;
    try {
      const result = await instance.acquireTokenSilent({
        ...loginRequest,
        account,
      });
      return result.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        try {
          const result = await instance.acquireTokenPopup(loginRequest);
          return result.accessToken;
        } catch (popupError) {
          console.error('Token acquisition failed:', popupError);
          return null;
        }
      }
      console.error('Token acquisition failed:', error);
      return null;
    }
  }, [instance, account]);

  return {
    isAuthenticated,
    user,
    login,
    logout,
    getAccessToken,
    account,
  };
}
